import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Check } from "lucide-react";

import SiteLayout from "@/components/SiteLayout";
import Icon from "@/components/Icon";
import PhaseStepper from "@/components/PhaseStepper";
import { HETS_PHASES, SERVICES, IMPACT } from "@/data/evara";

const EASE = [0.4, 0, 0.2, 1];

/* Reveal helper wrapping framer-motion. Fires once, ~80ms before element fully in view. */
function Reveal({ delay = 0, y = 20, children, className = "", once = true }) {
  const prefersReducedMotion = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: "-80px" }}
      transition={{ duration: 0.5, ease: EASE, delay: prefersReducedMotion ? 0 : delay }}
    >
      {children}
    </motion.div>
  );
}

function servicesForPhase(phaseNum) {
  return SERVICES.filter((s) => (s.frameworkPhases || []).includes(phaseNum));
}

/* --------------- Framework HUB view --------------- */
function FrameworkHub({ onSelectPhase }) {
  const prefersReducedMotion = useReducedMotion();
  return (
    <>
      <section className="section-pad bg-sage-gradient bg-hero-watermark" data-testid="framework-hero">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="eyebrow">Transformation Framework</div>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05]">
              A six-step journey from<br />
              <em className="not-italic text-[var(--evara-primary)] font-medium">
                insight to sustained change.
              </em>
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-lg text-[var(--evara-text-2)] leading-relaxed max-w-3xl">
              Analyze · Diagnose · Design · Implement · Train · Sustain. Hover any phase to see its purpose, click to explore what EVARA delivers inside it.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Big connected timeline */}
      <section className="section-pad pt-16 pb-8" data-testid="framework-timeline-section">
        <div className="max-w-6xl mx-auto">
          <div className="eyebrow mb-8">The journey</div>
          <div className="rounded-3xl border border-[var(--evara-border)] bg-white p-6 md:p-10">
            {/* Desktop */}
            <div className="hidden md:block">
              <PhaseStepper variant="full" onSelect={onSelectPhase} showTaglineOnHover />
            </div>
            {/* Mobile: vertical list */}
            <ol className="md:hidden space-y-4">
              {HETS_PHASES.map((p, i) => (
                <motion.li
                  key={p.num}
                  initial={prefersReducedMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.4, ease: EASE, delay: i * 0.05 }}
                >
                  <button
                    type="button"
                    onClick={() => onSelectPhase(i)}
                    className="w-full text-left rounded-2xl border border-[var(--evara-border)] bg-white p-4 flex items-start gap-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--evara-primary)]"
                    data-testid={`framework-mobile-phase-${p.num}`}
                  >
                    <span className="shrink-0 w-11 h-11 rounded-full border-2 border-[var(--evara-primary)] text-[var(--evara-primary)] font-serif flex items-center justify-center">
                      {p.num}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-lg text-[var(--evara-primary)]">{p.title}</div>
                      <p className="text-sm text-[var(--evara-text-2)] mt-1 leading-snug">{p.summary}</p>
                    </div>
                    <ChevronRight size={16} className="text-[var(--evara-muted)] shrink-0 mt-3" />
                  </button>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Detail card grid — click to explore */}
      <section className="section-pad pt-4" data-testid="framework-cards-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {HETS_PHASES.map((p, i) => (
              <motion.button
                key={p.num}
                type="button"
                onClick={() => onSelectPhase(i)}
                className="text-left rounded-2xl border border-[var(--evara-border)] bg-white p-6 hover:border-[var(--evara-border-strong)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--evara-primary)] group"
                initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.45, ease: EASE, delay: i * 0.06 }}
                whileHover={prefersReducedMotion ? {} : { y: -4 }}
                data-testid={`framework-card-${p.num}`}
              >
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-lg bg-[var(--evara-bg-mint)] text-[var(--evara-primary)] flex items-center justify-center">
                    <Icon name={p.icon} size={18} />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[var(--evara-muted)]">
                    Phase {p.num}
                  </span>
                </div>
                <div className="font-serif text-2xl mt-4 text-[var(--evara-primary)] leading-tight">
                  {p.title}
                </div>
                <p className="text-sm text-[var(--evara-text-2)] mt-2 leading-relaxed">{p.summary}</p>
                <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-semibold text-[var(--evara-primary)] group-hover:gap-3 transition-all">
                  Explore phase <ArrowRight size={14} />
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="section-pad bg-soft-sage" data-testid="framework-outcomes">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="eyebrow">Expected outcomes</div>
            <h2 className="font-serif text-3xl md:text-5xl mt-4 leading-tight">
              What the framework{" "}
              <em className="not-italic text-[var(--evara-primary)] font-medium">measurably produces.</em>
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mt-12">
            {IMPACT.map((o, i) => (
              <motion.div
                key={o.label}
                className="rounded-2xl border border-[var(--evara-border)] bg-white p-6"
                initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, ease: EASE, delay: i * 0.07 }}
              >
                <div className="font-serif text-4xl md:text-5xl text-[var(--evara-primary)] leading-none">
                  {o.stat}
                </div>
                <div className="mt-4 h-[1px] w-10 bg-[var(--evara-mint)]" />
                <div className="mt-3 text-sm text-[var(--evara-text-2)]">{o.label}</div>
              </motion.div>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-12">
              <Link to="/book" className="btn-primary" data-testid="framework-book-btn">
                Schedule a Consultation <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

/* --------------- Framework FOCUSED (single phase) view --------------- */
function FrameworkPhase({ idx, onSelectPhase }) {
  const prefersReducedMotion = useReducedMotion();
  const current = HETS_PHASES[idx];
  const prev = HETS_PHASES[(idx - 1 + HETS_PHASES.length) % HETS_PHASES.length];
  const next = HETS_PHASES[(idx + 1) % HETS_PHASES.length];
  const num = parseInt(current.num, 10);
  const services = servicesForPhase(num);

  return (
    <>
      <section className="section-pad bg-sage-gradient bg-hero-watermark" data-testid="framework-hero">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <Link
              to="/framework"
              className="inline-flex items-center gap-2 text-sm font-bold text-[var(--evara-primary)] hover:underline"
              data-testid="framework-view-all-link"
            >
              <ChevronLeft size={16} /> View all phases
            </Link>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="eyebrow mt-5">
              Framework · Step {current.num} of {HETS_PHASES.length}
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05]">
              <em className="not-italic text-[var(--evara-primary)] font-medium">{current.title}</em>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-lg text-[var(--evara-text-2)] leading-relaxed max-w-3xl">
              {current.summary}
            </p>
          </Reveal>
        </div>
      </section>

      {/* What happens + key activities */}
      <section className="section-pad pt-16" data-testid="framework-phase-body">
        <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Reveal>
              <div className="eyebrow">What happens in this phase</div>
              <p className="mt-4 text-[var(--evara-text-2)] leading-relaxed">
                {current.summary} EVARA teams work inside your operations during this phase, not from a slide deck, so the outputs reflect how your teams actually work and set the foundation for the next step.
              </p>
            </Reveal>
          </div>

          <div className="md:col-span-7">
            <Reveal>
              <div className="eyebrow mb-4">Key activities</div>
            </Reveal>
            <ul className="space-y-3">
              {current.bullets.map((b, i) => (
                <motion.li
                  key={b}
                  className="flex items-start gap-3 rounded-2xl border border-[var(--evara-border)] bg-white p-4"
                  initial={prefersReducedMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.4, ease: EASE, delay: i * 0.08 }}
                  data-testid={`framework-activity-${current.num}-${i}`}
                >
                  <span className="shrink-0 w-7 h-7 rounded-full bg-[var(--evara-bg-mint)] text-[var(--evara-primary)] flex items-center justify-center">
                    <Check size={14} />
                  </span>
                  <span className="text-sm md:text-base text-[var(--evara-text)] leading-relaxed pt-0.5">
                    {b}
                  </span>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Which services use this phase */}
      <section className="section-pad pt-4 pb-16" data-testid="framework-services-linkout">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-3xl border border-[var(--evara-border)] bg-[var(--evara-bg-soft)] p-6 md:p-10">
            <Reveal>
              <div className="eyebrow">Services drawing on this phase</div>
              <h2 className="font-serif text-2xl md:text-3xl mt-3 text-[var(--evara-primary)] leading-tight">
                {services.length} of our 7 practices use{" "}
                <em className="not-italic">{current.title}</em> as a core step.
              </h2>
            </Reveal>
            <div className="mt-6 flex flex-wrap gap-3">
              {services.map((s, i) => (
                <motion.div
                  key={s.key}
                  initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.35, ease: EASE, delay: i * 0.06 }}
                >
                  <Link
                    to={`/services?practice=${s.key}`}
                    className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold bg-white border border-[var(--evara-border)] text-[var(--evara-text)] hover:border-[var(--evara-primary)] hover:text-[var(--evara-primary)] transition-colors"
                    data-testid={`framework-service-chip-${current.num}-${s.key}`}
                  >
                    <Icon name={s.icon} size={14} />
                    {s.title}
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Phase nav (Prev / Next) with mini stepper */}
      <section className="section-pad pt-0 pb-16" data-testid="framework-phase-nav">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="rounded-3xl border border-[var(--evara-border)] bg-white p-6 md:p-8"
            initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, ease: EASE }}
          >
            <div className="mb-6">
              <PhaseStepper variant="compact" activeIndex={idx} onSelect={onSelectPhase} animateOnScroll={false} />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => onSelectPhase((idx - 1 + HETS_PHASES.length) % HETS_PHASES.length)}
                className="inline-flex items-center gap-3 text-left group"
                data-testid="framework-prev-btn"
              >
                <ChevronLeft size={18} className="text-[var(--evara-primary)]" />
                <span>
                  <span className="block text-[10px] uppercase tracking-[0.16em] font-semibold text-[var(--evara-muted)]">
                    Previous · Phase {prev.num}
                  </span>
                  <span className="block font-serif text-lg text-[var(--evara-primary)] group-hover:underline">
                    {prev.title}
                  </span>
                </span>
              </button>
              <Link to="/book" className="btn-primary" data-testid={`framework-phase-cta-${current.num}`}>
                Schedule a Consultation <ArrowRight size={16} />
              </Link>
              <button
                type="button"
                onClick={() => onSelectPhase((idx + 1) % HETS_PHASES.length)}
                className="inline-flex items-center gap-3 text-right group"
                data-testid="framework-next-btn"
              >
                <span>
                  <span className="block text-[10px] uppercase tracking-[0.16em] font-semibold text-[var(--evara-muted)]">
                    Next · Phase {next.num}
                  </span>
                  <span className="block font-serif text-lg text-[var(--evara-primary)] group-hover:underline">
                    {next.title}
                  </span>
                </span>
                <ChevronRight size={18} className="text-[var(--evara-primary)]" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}

/* --------------- Framework page shell --------------- */
export default function Framework() {
  const [params, setParams] = useSearchParams();
  const phaseParam = parseInt(params.get("phase"), 10);
  const focused = Number.isInteger(phaseParam) && phaseParam >= 1 && phaseParam <= HETS_PHASES.length;

  const setPhase = (idx0) => {
    setParams({ phase: String(idx0 + 1) });
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <SiteLayout>
      {focused ? (
        <FrameworkPhase idx={phaseParam - 1} onSelectPhase={setPhase} />
      ) : (
        <FrameworkHub onSelectPhase={setPhase} />
      )}
    </SiteLayout>
  );
}
